/**
 * What a FREE MOVE may translate — the move-group closure with the cabinet
 * run's own geometry taken out of it.
 *
 * `getMoveGroup` answers membership: everything the parent tree and the
 * fixture group tie together. A free translation is a narrower question,
 * because a run OWNS where its pieces sit — it re-emits its cabinets and
 * countertops from the run spec on every re-solve, and it re-seats its bays'
 * appliances along the wall (`moveApplianceObjectsToBays`). Writing a `center`
 * onto any of those is at best undone by the next run edit; across the wall
 * nothing ever corrects it.
 *
 * The tear that made this a rule (2026-09-08, Golden West / Scan 1): nudging
 * an island's faucet translated its whole closure — the basin and the sink
 * cabinet through the shared fixture group, and the run's COUNTERTOP through
 * that cabinet, since a run-spanning countertop names every one of its
 * cabinets in `parents[]` and the closure reads "names X" as "follows X". The
 * run's own fillers and end panels carry their own per-cabinet groups, so
 * they stayed exactly where they were and the island came apart. The faucet
 * itself now moves its bay (`runBayAnchoring`), but the leak is general: ANY
 * loose object grouped with run cabinetry could drag it off the run.
 *
 * A blocked object is not merely skipped — the walk does not pass THROUGH it.
 * If the sink cabinet is staying put, the countertop reachable only through
 * it has no business moving either.
 *
 * The root always travels: it is the object the user grabbed, and the panel
 * has already refused a free move for anything a run owns (`canFreeMove`).
 */

import { walkMoveGroup } from './moveGroup';
import { runBayAnchoring } from './cabinetRunGeometry';

/**
 * Is this object's position the RUN's to decide — as one of the boxes it
 * emits (`runId`), or as an appliance (or an appliance's accessory) anchored
 * by one of its bays?
 *
 * The bay side is asked through `runBayAnchoring` rather than the `bayId`
 * stamp, so a room stored before the stamp existed answers the same, and an
 * appliance whose run has since been deleted is correctly loose again.
 *
 * @param {Object} room — the room holding `object` (needs `cabinetRuns`).
 * @param {Object} object
 * @returns {boolean}
 */
export function isRunOwnedForMove(room, object) {
  if (!object) return false;
  if (object.runId) return true;
  return !!runBayAnchoring(room, object);
}

/**
 * The move-group closure of `rootId`, minus everything a cabinet run owns.
 *
 * @param {string} rootId
 * @param {Object} room — the room holding the objects (`objects`,
 *   `cabinetRuns`).
 * @returns {Set<string>}
 */
export function getFreeMoveGroup(rootId, room) {
  const objects = room?.objects || [];
  if (!room?.cabinetRuns?.length) return walkMoveGroup(rootId, objects);
  return walkMoveGroup(rootId, objects, (obj) => isRunOwnedForMove(room, obj));
}
